import { FormSection } from "@/components/persons/person-form/FormSection";
import type { PersonForm } from "@/components/persons/person-form/types";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import { FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";

/**
 * Where this person sits on the org chart, and who they report to.
 *
 * `personId` is the person being edited, excluded from the manager and
 * dotted-line pickers so nobody can be made to report to themselves. Absent
 * when adding someone new.
 */
export function RoleOverviewSection({ form, personId }: { form: PersonForm; personId?: string }) {
  // Role Team cascades from Department: a role team belongs to exactly one
  // department, so the picker is filtered to the chosen department and
  // disabled until one is set.
  const departmentId = form.watch("departmentId");
  const excludeIds = personId ? [personId] : undefined;

  return (
    <FormSection
      title="Role Overview"
      description="Department and role team place this person on the org chart. Pick a department first — role teams are filtered to it."
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <FormField
          control={form.control}
          name="departmentId"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>Department</FormLabel>
              <EntityCombobox
                entity="departments"
                value={field.value ?? null}
                onChange={(v) => {
                  // Changing department can invalidate the chosen role team
                  // (teams are department-scoped) — clear it so the form never
                  // carries a mismatched pair.
                  if (v !== field.value) {
                    form.setValue("roleTeamId", null, { shouldDirty: true });
                  }
                  field.onChange(v);
                }}
                includeNone
                placeholder="None"
                searchPlaceholder="Search departments…"
                aria-invalid={Boolean(fieldState.error)}
              />
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="roleTeamId"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>Role Team</FormLabel>
              <EntityCombobox
                entity="role-teams"
                value={field.value ?? null}
                onChange={field.onChange}
                filters={departmentId ? { departmentId } : undefined}
                disabled={!departmentId}
                includeNone
                placeholder={departmentId ? "None" : "Select a department first"}
                searchPlaceholder="Search role teams…"
                aria-invalid={Boolean(fieldState.error)}
              />
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="managerId"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>Manager</FormLabel>
              <EntityCombobox
                entity="persons"
                value={field.value ?? null}
                onChange={field.onChange}
                filters={{ status: "active" }}
                excludeIds={excludeIds}
                includeNone
                placeholder="None"
                searchPlaceholder="Search people…"
                aria-invalid={Boolean(fieldState.error)}
              />
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="dottedLineId"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>Dotted line</FormLabel>
              <EntityCombobox
                entity="persons"
                // Active only, matching the manager field above: a leaver
                // should not be offered as someone's mentor.
                filters={{ status: "active" }}
                value={field.value ?? null}
                onChange={field.onChange}
                excludeIds={excludeIds}
                includeNone
                placeholder="None"
                searchPlaceholder="Search people…"
                aria-invalid={Boolean(fieldState.error)}
              />
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </FormSection>
  );
}
